import Client from 'services/Client.provider';
import { iCodificationRest } from '../Codification/Codification.type';
import i18n from 'i18n';

export interface iCategoryRepository {
  getAll: (
    type: string,
    categoryId?: number | number[]
  ) => Promise<iCodificationRest[]>;
  getCategoryById: (id: string) => Promise<iCodificationRest>;
  createCategory: (data: any) => Promise<iCodificationRest>;
  updateCategory: (id: string, data: any) => Promise<iCodificationRest>;
  deleteCategory: (id: string) => Promise<void>;
  parentCategory: (type: string) => Promise<iCodificationRest[]>;
  childCategory: (parentId: number) => Promise<iCodificationRest[]>;
  importHierarchy: (
    file: File,
    onProgress?: (percent: number) => void
  ) => Promise<iHierarchyImportResult>;
  downloadHierarchyTemplate: () => Promise<Blob>;
}

export interface iHierarchyImportError {
  row: number;
  message: string;
}

export interface iHierarchyImportResult {
  message: string;
  stats: Record<
    'universes' | 'categories' | 'subcategories',
    { created: number; existing: number }
  >;
}

export class HierarchyImportError extends Error {
  constructor(message: string, public errors: iHierarchyImportError[]) {
    super(message);
  }
}

export class CategoryRepository extends Client implements iCategoryRepository {
  public async getAll(
    type: string,
    categoryId: number | number[] = 0
  ): Promise<iCodificationRest[]> {
    // Get the current active language
    const currentLanguage = i18n.language;
    // Several parent categories (multi-category exhibitors) or a single legacy id
    const categoryIds = (Array.isArray(categoryId) ? categoryId : [categoryId])
      .map(Number)
      .filter((id) => id > 0);
    return await this.restricted()
      .get<iCodificationRest[]>(`/categories`, {
        params: {
          locale: currentLanguage,
          type: type,
          category_ids: categoryIds.length > 0 ? categoryIds : undefined,
        },
      })
      .then((e) => {
        return e.data;
      })
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            JSON.stringify(error.response.data)
              .replace(/[{}[\]"]/g, '')
              .replace(/,/g, '<br/>') ||
            `${error.response.status} | Unknow error...`
        );
      });
  }

  public async getCategoryById(id: string): Promise<iCodificationRest> {
    return await this.restricted()
      .get<iCodificationRest>(`/categories/${id}`)
      .then((e) => e.data);
  }

  public async createCategory(data: any): Promise<iCodificationRest> {
    return await this.restricted()
      .post<iCodificationRest>(`/categories`, data)
      .then((e) => e.data);
  }

  public async updateCategory(
    id: string,
    data: any
  ): Promise<iCodificationRest> {
    return await this.restricted()
      .put<any>(`/categories/${id}`, data)
      .then((e) => e.data);
  }

  public async deleteCategory(id: string): Promise<void> {
    return await this.restricted()
      .delete(`/categories/${id}`)
      .then(() => undefined)
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            JSON.stringify(error.response.data)
              .replace(/[{}[\]"]/g, '')
              .replace(/,/g, '<br/>') ||
            `${error.response.status} | Unknown error...`
        );
      });
  }

  public async parentCategory(type: string): Promise<iCodificationRest[]> {
    // Get the current active language
    const currentLanguage = i18n.language;
    return await this.restricted()
      .get<iCodificationRest[]>(`/parent-categories`, {
        params: {
          locale: currentLanguage,
          type: type,
        },
      })
      .then((e) => {
        return e.data;
      })
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            JSON.stringify(error.response.data)
              .replace(/[{}[\]"]/g, '')
              .replace(/,/g, '<br/>') ||
            `${error.response.status} | Unknow error...`
        );
      });
  }

  public async childCategory(parentId: number): Promise<iCodificationRest[]> {
    // Get the current active language
    const currentLanguage = i18n.language;
    return await this.restricted()
      .get<iCodificationRest[]>(`/child-categories`, {
        params: {
          locale: currentLanguage,
          parent_id: parentId,
        },
      })
      .then((e) => {
        return e.data;
      })
      .catch((error) => {
        throw new Error(
          error.response?.data?.message ||
            JSON.stringify(error.response.data)
              .replace(/[{}[\]"]/g, '')
              .replace(/,/g, '<br/>') ||
            `${error.response.status} | Unknow error...`
        );
      });
  }

  public async importHierarchy(
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<iHierarchyImportResult> {
    const form = new FormData();
    form.append('file', file);
    return await this.restricted()
      .post<iHierarchyImportResult>(`/categories/import-hierarchy`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      })
      .then((e) => e.data)
      .catch((error) => {
        const data = error.response?.data;
        const errors: iHierarchyImportError[] = Array.isArray(data?.errors)
          ? data.errors
          : [];
        throw new HierarchyImportError(
          data?.message ?? error.message ?? 'Unknown error',
          errors
        );
      });
  }

  public async downloadHierarchyTemplate(): Promise<Blob> {
    return await this.restricted()
      .get(`/categories/import-hierarchy/template`, { responseType: 'blob' })
      .then((e) => e.data);
  }
}
